/**
 * Chart Label Helpers - Kanonische Kurzlabels für Charts
 * 
 * Regeln:
 * - Strecken: Nur Substrecken-Name wenn vorhanden, sonst Hauptstrecke
 * - Maschinen: Nur Maschinenname, kein "WSA:" Prefix
 * - Keine Prefixe wie "Strecke:", keine Trenner "→"
 * - Tooltip zeigt vollständigen Namen
 */

/**
 * Extrahiert Kurzlabel aus Strecken-String
 * 
 * Beispiele:
 * - "Strecke: Mangeln → Groß" → "Groß"
 * - "Strecke: Mangeln" → "Mangeln"
 * - "Mangeln → Tischwäsche" → "Tischwäsche"
 * - "Mangeln" → "Mangeln"
 */
export function getChartLabelForLine(locationString: string | null | undefined): string {
  if (!locationString) return 'Unbekannt';
  
  let cleaned = locationString.trim();
  
  // Prefix "Strecke: " entfernen
  if (cleaned.startsWith('Strecke: ')) {
    cleaned = cleaned.substring(9).trim();
  }
  
  // Bei "→" nur den Teil nach dem letzten Pfeil (Substrecke)
  if (cleaned.includes('→')) {
    const parts = cleaned.split('→');
    const subLine = parts[parts.length - 1].trim();
    // Nur zurückgeben wenn nicht leer
    if (subLine) return subLine;
  }
  
  return cleaned;
}

/**
 * Extrahiert Kurzlabel aus Maschinen-String
 * 
 * Beispiele:
 * - "WSA: 17kg WSA" → "17kg WSA"
 * - "WSA: Waschstraße 1" → "Waschstraße 1"
 * - "17kg WSA" → "17kg WSA"
 */
export function getChartLabelForMachine(nameOrLocation: string | null | undefined): string {
  if (!nameOrLocation) return 'Unbekannt';
  
  let cleaned = nameOrLocation;
  
  // Prefix "WSA: " entfernen
  if (cleaned.startsWith('WSA: ')) {
    cleaned = cleaned.substring(5);
  }
  
  return cleaned.trim();
}

/**
 * Gibt Kundenname zurück (keine Transformation, aber Fallback)
 */
export function getChartLabelForCustomer(name: string | null | undefined): string {
  if (!name) return 'Unbekannt';
  return name.trim();
}

/**
 * Gibt Programmname zurück (keine Transformation, aber Fallback)
 */
export function getChartLabelForProgram(name: string | null | undefined): string {
  if (!name) return 'Unbekannt';
  return name.trim();
}

/**
 * Generisches Label aus location/machine-String
 * Erkennt automatisch ob Strecke oder Maschine
 */
export function getChartLabel(locationOrName: string | null | undefined): string {
  if (!locationOrName) return 'Unbekannt';
  
  if (locationOrName.startsWith('Strecke:') || locationOrName.includes('→')) {
    return getChartLabelForLine(locationOrName);
  }
  
  if (locationOrName.startsWith('WSA:')) {
    return getChartLabelForMachine(locationOrName);
  }
  
  return locationOrName.trim();
}

/**
 * Wraps a label into multiple lines for chart display
 * 
 * @param label - The label text to wrap
 * @param maxCharsPerLine - Maximum characters per line
 * @param maxLines - Maximum number of lines (default: 2)
 * @returns Array of line strings
 */
export function wrapLabel(label: string, maxCharsPerLine: number, maxLines: number = 2): string[] {
  if (!label) return [''];
  if (label.length <= maxCharsPerLine) return [label];
  
  const words = label.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    // Handle words longer than maxCharsPerLine
    if (word.length > maxCharsPerLine) {
      // Finish current line if any
      if (currentLine) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
      
      // Hard-split the long word
      let remaining = word;
      while (remaining.length > 0 && lines.length < maxLines) {
        if (remaining.length <= maxCharsPerLine) {
          currentLine = remaining;
          remaining = '';
        } else {
          lines.push(remaining.substring(0, maxCharsPerLine - 1) + '-');
          remaining = remaining.substring(maxCharsPerLine - 1);
        }
      }
      
      // If we hit maxLines, truncate with ellipsis
      if (lines.length >= maxLines && remaining.length > 0) {
        const lastLine = lines[lines.length - 1];
        if (lastLine.length > maxCharsPerLine - 1) {
          lines[lines.length - 1] = lastLine.substring(0, maxCharsPerLine - 1) + '…';
        }
        return lines;
      }
      continue;
    }
    
    // Normal word handling
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      // Current line is full, start new line
      if (currentLine) {
        lines.push(currentLine.trim());
      }
      
      // Check if we've hit maxLines
      if (lines.length >= maxLines) {
        // Truncate last line with ellipsis if needed
        const lastLine = lines[lines.length - 1];
        if (lastLine.length > maxCharsPerLine - 1) {
          lines[lines.length - 1] = lastLine.substring(0, maxCharsPerLine - 1) + '…';
        }
        return lines;
      }
      
      currentLine = word;
    }
  }
  
  // Add remaining content
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine.trim());
  } else if (currentLine && lines.length >= maxLines) {
    // Append to last line with truncation
    const lastIdx = lines.length - 1;
    const combined = lines[lastIdx] + ' ' + currentLine;
    if (combined.length > maxCharsPerLine) {
      lines[lastIdx] = combined.substring(0, maxCharsPerLine - 1) + '…';
    } else {
      lines[lastIdx] = combined;
    }
  }
  
  return lines.length > 0 ? lines : [label.substring(0, maxCharsPerLine - 1) + '…'];
}
